import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink, Loader2 } from 'lucide-react';

import { attendanceApi } from '../../services/api';
import type { PayrollShift, PayrollShiftIssue } from '../../types';
import { formatDateKey, formatWeekday } from '../../utils/money';
import { Button, Modal, StatusBadge } from '../ui';

type StudentStatus = 'present' | 'late' | 'absent' | 'excused' | 'not_marked';

interface SessionPerson {
    firstName?: string | null;
    lastName?: string | null;
}

interface SessionStudent {
    id: string;
    name: string;
    status: StudentStatus;
    isTemporary?: boolean;
    lateArrivalTime?: string | null;
    note?: string;
    lateNote?: string;
    absentNote?: string;
    excusedNote?: string;
}

interface SessionDetail {
    attendance: {
        notes?: string | null;
        teacher?: SessionPerson | null;
        assistant?: SessionPerson | null;
        teacherStartTime?: string | null;
        teacherEndTime?: string | null;
        assistantStartTime?: string | null;
        assistantEndTime?: string | null;
    } | null;
    students: SessionStudent[];
}

// Same words as the roll call on the attendance tab.
const statusLabels: Record<StudentStatus, string> = {
    present: 'Có mặt',
    late: 'Muộn',
    absent: 'Vắng',
    excused: 'Có phép',
    not_marked: 'Chưa điểm danh',
};
const statusTones = { present: 'success', late: 'warning', absent: 'danger', excused: 'info', not_marked: 'neutral' } as const;
const STATUS_ORDER: StudentStatus[] = ['present', 'late', 'absent', 'excused', 'not_marked'];

const slotLabels = { TEACHER: 'Giảng dạy', ASSISTANT: 'Trợ giảng' } as const;

function fullName(person?: SessionPerson | null) {
    return person ? `${person.firstName ?? ''} ${person.lastName ?? ''}`.trim() || null : null;
}

function hours(start?: string | null, end?: string | null) {
    return start && end ? `${start}–${end}` : null;
}

function studentNote(student: SessionStudent) {
    const byStatus = { late: student.lateNote, absent: student.absentNote, excused: student.excusedNote }[student.status as 'late' | 'absent' | 'excused'];
    const lateAt = student.status === 'late' && student.lateArrivalTime
        ? `Đến ${new Date(student.lateArrivalTime).toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Ho_Chi_Minh' })}`
        : null;
    return [lateAt, byStatus, student.note].filter(Boolean).join(' · ');
}

// The session behind a payroll shift, read-only; editing stays on the attendance tab.
export function ShiftSessionModal({ shift, href, onOpenIssue, onClose }: {
    shift: PayrollShift | null;
    href: string;
    // Opens the issue's notification (admins and managers only).
    onOpenIssue?: (issue: PayrollShiftIssue) => void;
    onClose: () => void;
}) {
    const [detail, setDetail] = useState<SessionDetail | null>(null);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!shift) return;
        let active = true;
        setDetail(null);
        setError('');
        attendanceApi.getByClass(shift.classId, shift.date)
            .then(response => active && setDetail(response.data.data))
            .catch((err: any) => active && setError(err?.message || 'Không tải được buổi học.'));
        return () => {
            active = false;
        };
    }, [shift]);

    if (!shift) return null;
    const session = detail?.attendance;
    const students = detail?.students ?? [];
    const counts = STATUS_ORDER.map(status => ({ status, count: students.filter(student => student.status === status).length }))
        .filter(item => item.count > 0);
    const staff = [
        { label: 'Giáo viên', name: fullName(session?.teacher), time: hours(session?.teacherStartTime, session?.teacherEndTime) },
        { label: 'Trợ giảng', name: fullName(session?.assistant), time: hours(session?.assistantStartTime, session?.assistantEndTime) },
    ];

    return (
        <Modal
            open
            title={`${shift.className} · ${formatDateKey(shift.date)}`}
            description={[formatWeekday(shift.date), shift.campusName, shift.scheduledStart && shift.scheduledEnd ? `Lịch ${shift.scheduledStart}–${shift.scheduledEnd}` : null].filter(Boolean).join(' · ')}
            onClose={onClose}
            footer={(
                <div className="flex flex-wrap justify-end gap-2">
                    <Link
                        to={href}
                        className="inline-flex min-h-9 items-center gap-1.5 rounded-md border border-ink-200 bg-white px-3 text-sm font-medium text-ink-700 hover:bg-ink-50"
                    >
                        <ExternalLink className="h-4 w-4" />
                        Mở trang điểm danh
                    </Link>
                    <Button onClick={onClose}>Đóng</Button>
                </div>
            )}
        >
            <div className="space-y-5 text-sm">
                <section className="space-y-2">
                    <h3 className="font-semibold text-ink-900">Ca {slotLabels[shift.slot].toLowerCase()}</h3>
                    <p className="text-ink-700">
                        {hours(shift.actualStart, shift.actualEnd) ?? '—'}
                        {shift.shiftTypeName ? ` · ${shift.shiftTypeName}` : ''}
                    </p>
                    {shift.issues?.length ? (
                        <ul className="space-y-1">
                            {shift.issues.map(issue => (
                                <li key={`${issue.kind}-${issue.activityId}`} className="text-xs">
                                    <span className="inline-flex items-center gap-1 font-medium text-warning-700">
                                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                                        {onOpenIssue && issue.activityId ? (
                                            <button type="button" onClick={() => onOpenIssue(issue)} className="hover:underline">{issue.label}</button>
                                        ) : issue.label}
                                    </span>
                                    {issue.detail ? <span className="ml-1 text-ink-500">{issue.detail}</span> : null}
                                </li>
                            ))}
                        </ul>
                    ) : null}
                </section>

                {error ? (
                    <p className="rounded-md bg-danger-50 px-3 py-2 text-danger-700">{error}</p>
                ) : !detail ? (
                    <p className="flex items-center gap-2 text-ink-500"><Loader2 className="h-4 w-4 animate-spin" />Đang tải buổi học</p>
                ) : (
                    <>
                        <section className="grid gap-3 sm:grid-cols-2">
                            {staff.map(person => (
                                <div key={person.label} className="rounded-lg border border-ink-200 px-3 py-2">
                                    <p className="text-xs text-ink-500">{person.label}</p>
                                    <p className="font-medium text-ink-900">{person.name ?? 'Chưa có'}</p>
                                    {person.name && person.time ? <p className="text-xs tabular-nums text-ink-500">{person.time}</p> : null}
                                </div>
                            ))}
                        </section>

                        <section className="space-y-2">
                            <div className="flex flex-wrap items-center gap-2">
                                <h3 className="font-semibold text-ink-900">Học sinh ({students.length})</h3>
                                {counts.map(({ status, count }) => (
                                    <StatusBadge key={status} tone={statusTones[status]}>{statusLabels[status]} {count}</StatusBadge>
                                ))}
                            </div>
                            {students.length ? (
                                <ul className="divide-y divide-ink-100 rounded-lg border border-ink-200">
                                    {students.map(student => {
                                        const note = studentNote(student);
                                        return (
                                            <li key={student.id} className="flex items-start justify-between gap-3 px-3 py-2">
                                                <div className="min-w-0">
                                                    <p className="font-medium text-ink-900">
                                                        {student.name}
                                                        {student.isTemporary ? <span className="ml-1.5 text-xs font-normal text-ink-500">Học bù</span> : null}
                                                    </p>
                                                    {note ? <p className="text-xs text-ink-500">{note}</p> : null}
                                                </div>
                                                <StatusBadge tone={statusTones[student.status] ?? 'neutral'}>{statusLabels[student.status] ?? student.status}</StatusBadge>
                                            </li>
                                        );
                                    })}
                                </ul>
                            ) : (
                                <p className="text-ink-500">Lớp chưa có học sinh.</p>
                            )}
                        </section>

                        {session?.notes ? (
                            <section className="space-y-1">
                                <h3 className="font-semibold text-ink-900">Ghi chú buổi học</h3>
                                <p className="whitespace-pre-line text-ink-700">{session.notes}</p>
                            </section>
                        ) : null}
                    </>
                )}
            </div>
        </Modal>
    );
}
